import { Alert, Badge, Button, Card, Group, Stack, Table, Text, Title } from '@mantine/core';
import { useNavigate, useParams } from 'react-router';
import {
  useKanaatBelgesiUret,
  useSaglikEkSil,
  useSaglikEkYukle,
  useSaglikKanaatSil,
  useSaglikKanaati,
} from '../api/queries';
import { QueryStatus } from './parts';
import { KapiSebebi } from '../sozlesme';
import { ANLAM } from '../theme';
import { MUAYENE_ASAMASI_ETIKETI } from '@isg/validation';
import { tarihGoster } from '../lib/format';

/**
 * Kanaat detayı (F3B·S8 — Kapsam §16.11).
 *
 * **DÜĞME GÖRÜNÜRLÜĞÜ SUNUCUDAN** (`yetkiler.duzenleyebilir` — tenant
 * `saglik_kanaat_giris` listesi ∧ bahçe): ekran ikinci kural yazmaz;
 * görünmeyen düğmenin ucu da aynı kapıdan 403 verir.
 *
 * **GEREKÇE YOKTUR** (S1): tanı/tetkik/bulgu gösterilmez, tutulmaz. Tetkik
 * satırı yalnız "yapıldı mı" işaretini gösterir.
 */
export function KanaatDetayScreen(): React.JSX.Element {
  const { opinionId } = useParams();
  const kanaat = useSaglikKanaati(opinionId ?? null);
  const ekYukle = useSaglikEkYukle();
  const ekSil = useSaglikEkSil();
  const sil = useSaglikKanaatSil();
  const belgeUret = useKanaatBelgesiUret(opinionId ?? '');
  const navigate = useNavigate();
  const kayit = kanaat.data;

  const tetkikler = kayit
    ? Object.entries(kayit.tetkikler)
        .filter(([, v]) => v)
        .map(([k]) => TETKIK_ETIKET[k] ?? k)
    : [];

  return (
    <Stack maw={900}>
      <QueryStatus queries={[kanaat]} kayit />
      {kayit && (
        <>
          <Group justify="space-between">
            <Title order={3}>{kayit.personAd} — sağlık kanaati</Title>
            {kayit.yetkiler.duzenleyebilir && (
              <Button
                color={ANLAM.engel}
                variant="light"
                data-testid="kanaat-sil"
                onClick={() =>
                  sil.mutate({ id: kayit.id }, { onSuccess: () => void navigate('/saglik') })
                }
              >
                Kaydı sil
              </Button>
            )}
          </Group>

          {kayit.vadesiDoldu && (
            <Alert color={ANLAM.engel} data-testid="kanaat-vade-bandi">
              Kanaatin geçerlilik süresi dolmuş — bu kişi iş izninde <b>uygun sayılmaz</b>.
            </Alert>
          )}

          <Card withBorder>
            <Table>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td w={220}>İşyeri</Table.Td>
                  <Table.Td>{kayit.workplaceAd}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Görev (bilgi)</Table.Td>
                  <Table.Td>{kayit.gorevMetni ?? '—'}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Muayene türü</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm">{kayit.muayeneTuru}</Text>
                      {/* S1: aşama TÜRETİLİR (kişinin bu işyerindeki ilk kaydı
                          mı) — kayıtta seçilen bir değer DEĞİLDİR. */}
                      <Badge variant="light" color={ANLAM.bilgi} data-testid="kanaat-detay-asama">
                        {MUAYENE_ASAMASI_ETIKETI[kayit.muayeneAsamasi]}
                      </Badge>
                    </Group>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Muayene tarihi</Table.Td>
                  <Table.Td>{tarihGoster(kayit.muayeneTarihi)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Geçerlilik bitişi</Table.Td>
                  <Table.Td data-testid="kanaat-bitis-degeri">
                    {tarihGoster(kayit.gecerlilikBitis)}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Karar</Table.Td>
                  <Table.Td>
                    <Badge color={KARAR_RENGI[kayit.kararSinifi ?? 'yok']}>
                      {kayit.uygunlukKarari}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Çalışma koşulları</Table.Td>
                  {/* B7 — yalnız İŞARETLİ kalemler; cümle sunucudan (`ifade`,
                      tek okuyucu), sorulmamış kalem listede yoktur. */}
                  <Table.Td data-testid="kanaat-detay-kisitlar">
                    {kayit.kisitlar.length === 0 ? (
                      '—'
                    ) : (
                      <Group gap="xs">
                        {kayit.kisitlar.map((k) => (
                          <Badge
                            key={k.id}
                            color={k.isaret === 'uygun' ? ANLAM.olumlu : ANLAM.dikkat}
                            variant="light"
                            data-testid={`kanaat-detay-kisit-${k.isaret}`}
                          >
                            {k.ifade}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Hekim</Table.Td>
                  <Table.Td>{kayit.hekimPersonAd ?? kayit.hekimAd ?? '—'}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Yapılan tetkikler</Table.Td>
                  <Table.Td>
                    {tetkikler.length === 0 ? '—' : tetkikler.join(' · ')}
                    <Text size="xs" c="dimmed">
                      “Yapıldı mı” bilgisidir — sonuçlar sistemde tutulmaz.
                    </Text>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Notlar</Table.Td>
                  <Table.Td>{kayit.notlar ?? '—'}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Card>

          <Card withBorder>
            <Title order={5}>Kanaat belgesi</Title>
            <Text size="sm" c="dimmed">
              Sistem ıslak imzaya hazır bir rapor üretir; dışarıdan gelen imzalı rapor da
              yüklenebilir. Belgeler ayrı bir sağlık deposunda tutulur ve her görüntüleme kayda
              geçer.
            </Text>
            {/* P13 — `saglik_kanaat_giris` listesi → bahçe; liste SUNUCUDAN (tenant ayarı). */}
            <KapiSebebi
              durum={kayit.yetkiler.yazmaKapisi}
              nesne="Kanaat belgesini"
              fiil="üretir ve yükler"
              testId="kanaat-yazamama-sebebi"
            />
            {kayit.yetkiler.duzenleyebilir && (
              <Group mt="sm">
                <Button
                  size="xs"
                  data-testid="kanaat-belge-uret"
                  loading={belgeUret.isPending}
                  onClick={() => belgeUret.mutate()}
                >
                  Kanaat raporu üret
                </Button>
                {belgeUret.isError && (
                  <Text size="xs" c={ANLAM.engel} role="alert" data-testid="kanaat-belge-hata">
                    {belgeUret.error.message}
                  </Text>
                )}
              </Group>
            )}
            <Stack gap="xs" mt="sm" data-testid="kanaat-ekler">
              {kayit.ekler.map((ek) => (
                <Group key={ek.id} justify="space-between">
                  <Group gap="xs">
                    <Text size="sm">{ek.dosyaAdi}</Text>
                    {/* Kayıt hangisinin ne olduğunu SÖYLER (P5). */}
                    <Badge
                      size="xs"
                      variant="light"
                      color={ANLAM.bilgi}
                      data-testid={`kanaat-ek-kaynak-${ek.id}`}
                    >
                      {ek.kaynak === 'uretilmis' ? 'Sistem üretti' : 'Yüklendi'}
                    </Badge>
                  </Group>
                  {kayit.yetkiler.duzenleyebilir && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color={ANLAM.engel}
                      onClick={() => ekSil.mutate({ id: kayit.id, ekId: ek.id })}
                    >
                      Sil
                    </Button>
                  )}
                </Group>
              ))}
              {kayit.ekler.length === 0 && <Text size="sm">Belge yok.</Text>}
              {kayit.yetkiler.duzenleyebilir && (
                <input
                  type="file"
                  data-testid="kanaat-ek-yukle"
                  onChange={(e) => {
                    const dosya = e.currentTarget.files?.[0];
                    if (dosya) ekYukle.mutate({ id: kayit.id, dosya });
                  }}
                />
              )}
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}

const KARAR_RENGI: Record<string, string> = {
  uygun: ANLAM.olumlu,
  kisitli: ANLAM.dikkat,
  uygun_degil: ANLAM.engel,
  yok: ANLAM.bilgi,
};

const TETKIK_ETIKET: Record<string, string> = {
  rontgen: 'Röntgen',
  isitme: 'İşitme',
  sft: 'SFT',
  kan: 'Kan',
  idrar: 'İdrar',
};
